<template>
  <b-container fluid="xs">
    <b-row>
      <b-col>
        <hr>
        <h5>Member Pricing Tiers</h5>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div class="d-flex justify-content-end mb-3">
          <button 
            type="button"
            class="btn btn-primary btn-sm"
            @click="showAddTierModal"
            :disabled="!priceScheduleId"
          >
            <i class="fas fa-plus"></i> Add Member Tier
          </button>
        </div>
        <div v-if="loading" class="text-center">
          <i class="fas fa-spinner fa-spin"></i> Loading member tiers...
        </div>
        
        <div v-else-if="memberTiers.length === 0" class="text-center text-muted">
          No member tiers configured. Click "Add Member Tier" to create one.
        </div>
        
        <div v-else class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Tier Name</th>
                <th>Stripe Product ID</th>
                <th>Member Price Schedule</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tier in sortedMemberTiers" :key="tier.id" :class="{ 'table-secondary': !tier.isActive }">
                <td :class="{ 'text-muted': !tier.isActive }">
                  {{ tier.tierName }}
                </td>
                <td :class="{ 'text-muted': !tier.isActive }">
                  <code>{{ tier.stripeProductId }}</code>
                </td>
                <td :class="{ 'text-muted': !tier.isActive }">{{ tier.memberPriceSchedule?.name || 'No Price Schedule' }}</td>
                <td>
                  <span class="badge badge-info">{{ tier.priority }}</span>
                </td>
                <td>
                  <span :class="tier.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ tier.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <button 
                    type="button"
                    class="btn btn-sm btn-outline-primary mr-1"
                    @click="editTier(tier)"
                    :title="tier.isActive ? 'Edit tier' : 'Edit and reactivate tier'"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    v-if="tier.isActive"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="deleteTier(tier)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                  <button 
                    v-else
                    type="button"
                    class="btn btn-sm btn-outline-success"
                    @click="reactivateTier(tier)"
                    title="Reactivate tier"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>
    </b-row>

    <!-- Add/Edit Modal -->
    <b-modal 
      :visible="showModal"
      :title="editingTier?.id ? 'Edit Member Tier' : 'Add Member Tier'"
      size="lg"
      @hidden="hideModal"
      @ok="saveTier"
      :ok-disabled="saving || !isFormValid"
      :ok-title="editingTier?.id ? 'Update Member Tier' : 'Save Member Tier'"
      cancel-title="Cancel"
    >
      <div>
            <form @submit.prevent="saveTier">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Tier Name *</label>
                    <input 
                      v-model="editingTier.tierName"
                      type="text"
                      class="form-control"
                      placeholder="e.g., Premium Member"
                      required
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>
                      Priority *
                      <HelpPopup>
                        Higher priority tiers are used when patients have multiple subscriptions
                      </HelpPopup>
                    </label>
                    <input 
                      v-model.number="editingTier.priority"
                      type="number"
                      class="form-control"
                      placeholder="Higher number = higher priority"
                      required
                      min="0"
                    >
                  </div>
                </div>
              </div>
              
              <div class="form-group">
                <label>
                  Stripe Product *
                  <HelpPopup>
                    The Stripe subscription product for the membership tier
                  </HelpPopup>
                </label>
                <select 
                  v-model="editingTier.stripeProductId"
                  class="form-control"
                  required
                  @change="validateStripeProduct"
                  :disabled="loadingStripeProducts"
                >
                  <option value="">Select a Stripe product</option>
                  <option 
                    v-for="product in stripeProducts" 
                    :key="product.id"
                    :value="product.id"
                  >
                    {{ product.name }} ({{ product.id }})
                  </option>
                </select>
                <div v-if="loadingStripeProducts" class="text-muted small">
                  <i class="fas fa-spinner fa-spin"></i> Loading Stripe products...
                </div>
                <div v-if="stripeProductValidation.error" class="text-danger">
                  {{ stripeProductValidation.error }}
                </div>
              </div>
              
              <div class="form-group">
                <label>
                  Member Price Schedule *
                  <HelpPopup>
                    The price schedule to use for members with this subscription
                  </HelpPopup>
                </label>
                <select 
                  v-model.number="editingTier.memberPriceScheduleId"
                  class="form-control"
                  required
                >
                  <option value="">Select a price schedule</option>
                  <option 
                    v-for="schedule in availablePriceSchedules" 
                    :key="schedule.id"
                    :value="schedule.id"
                  >
                    {{ schedule.name }}
                  </option>
                </select>
              </div>
              
              <div class="form-group">
                <label>
                  Active
                  <HelpPopup>
                    Only active member tiers will be used for subscription pricing
                  </HelpPopup>
                </label>
                <div class="form-check">
                  <b-form-checkbox 
                    v-model="editingTier.isActive"
                    size="sm"
                  >
                    Enable this member tier
                  </b-form-checkbox>
                </div>
              </div>
            </form>
      </div>
      <template #modal-ok>
        <i v-if="saving" class="fas fa-spinner fa-spin"></i>
        {{ editingTier?.id ? 'Update' : 'Save' }} Member Tier
      </template>
    </b-modal>
  </b-container>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { PriceScheduleMemberTier } from '@/models/PriceScheduleMemberTier';
import { PriceSchedule } from '@/models/PriceSchedule';
import { StripeProduct } from '@/models/StripeProduct';
import HelpPopup from '@/components/HelpPopup.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import axios from 'axios';

@Component({
  components: {
    HelpPopup
  }
})
export default class MemberTierManagement extends Vue {
  @Prop({ required: true })
  priceScheduleId!: number;

  memberTiers: PriceScheduleMemberTier[] = [];
  availablePriceSchedules: PriceSchedule[] = [];
  editingTier: PriceScheduleMemberTier = new PriceScheduleMemberTier();
  stripeProducts: StripeProduct[] = [];
  
  loading = false;
  saving = false;
  showModal = false;
  loadingStripeProducts = false;
  stripeProductValidation = { error: '', isValidating: false };

  get sortedMemberTiers(): PriceScheduleMemberTier[] {
    return [...this.memberTiers].sort((a, b) => b.priority - a.priority);
  }

  get isFormValid(): boolean {
    return !!(
      this.editingTier.tierName &&
      this.editingTier.stripeProductId &&
      this.editingTier.memberPriceScheduleId &&
      !this.stripeProductValidation.error
    );
  }

  @Watch('priceScheduleId', { immediate: true })
  async onPriceScheduleIdChanged() {
    if (this.priceScheduleId) {
      await this.loadMemberTiers();
    }
  }

  async mounted() {
    await this.loadPriceSchedules();
    await this.loadStripeProducts();
  }

  async loadMemberTiers() {
    if (!this.priceScheduleId) return;
    
    this.loading = true;
    try {
      const response = await axios.get(`/api/priceschedule/${this.priceScheduleId}/member-tiers`);
      this.memberTiers = response.data;
    } catch (error) {
      console.error('Error loading member tiers:', error);
      this.$notification(NotificationOptions.error('Failed to load member tiers'));
    } finally {
      this.loading = false;
    }
  }

  async loadPriceSchedules() {
    try {
      const response = await axios.get('/api/priceschedule');
      this.availablePriceSchedules = response.data.filter(
        (schedule: PriceSchedule) => schedule.id !== this.priceScheduleId
      );
    } catch (error) {
      console.error('Error loading price schedules:', error);
    }
  }

  async loadStripeProducts() {
    this.loadingStripeProducts = true;
    try {
      const response = await axios.get('/api/priceschedule/stripe-subscription-products');
      this.stripeProducts = response.data;
    } catch (error) {
      console.error('Error loading Stripe products:', error);
      this.$notification(NotificationOptions.error('Failed to load Stripe products. Please check your Stripe configuration.'));
    } finally {
      this.loadingStripeProducts = false;
    }
  }

  showAddTierModal() {
    this.editingTier = new PriceScheduleMemberTier({
      priceScheduleId: this.priceScheduleId,
      priority: this.memberTiers.length + 1
    });
    this.stripeProductValidation = { error: '', isValidating: false };
    this.showModal = true;
  }

  editTier(tier: PriceScheduleMemberTier) {
    this.editingTier = new PriceScheduleMemberTier(tier);
    this.stripeProductValidation = { error: '', isValidating: false };
    this.showModal = true;
  }

  async saveTier() {
    if (!this.isFormValid) return;

    this.saving = true;
    try {
      let response;
      if (this.editingTier.id) {
        response = await axios.put(`/api/priceschedule/member-tiers/${this.editingTier.id}`, this.editingTier);
      } else {
        response = await axios.post(`/api/priceschedule/${this.priceScheduleId}/member-tiers`, this.editingTier);
      }

      this.$notification(NotificationOptions.successSaveNotificationPreset('Member tier'));

      this.showModal = false;
      await this.loadMemberTiers();
      
      // Emit event to notify parent component
      this.$emit('member-tier-saved');
    } catch (error: any) {
      console.error('Error saving member tier:', error);
      this.$notification(NotificationOptions.error(error.response?.data?.message || 'Failed to save member tier'));
    } finally {
      this.saving = false;
    }
  }

  async deleteTier(tier: PriceScheduleMemberTier) {
    if (!confirm(`Are you sure you want to delete the "${tier.tierName}" member tier?`)) {
      return;
    }

    try {
      await axios.delete(`/api/priceschedule/member-tiers/${tier.id}`);
      this.$notification(NotificationOptions.successSaveNotificationPreset('Member tier deletion'));
      await this.loadMemberTiers();
      
      // Emit event to notify parent component
      this.$emit('member-tier-deleted');
    } catch (error: any) {
      console.error('Error deleting member tier:', error);
      this.$notification(NotificationOptions.error('Failed to delete member tier'));
    }
  }

  async reactivateTier(tier: PriceScheduleMemberTier) {
    if (!confirm(`Are you sure you want to reactivate the "${tier.tierName}" member tier?`)) {
      return;
    }

    try {
      const reactivatedTier = { ...tier, isActive: true };
      await axios.put(`/api/priceschedule/member-tiers/${tier.id}`, reactivatedTier);
      this.$notification(NotificationOptions.successSaveNotificationPreset('Member tier reactivation'));
      await this.loadMemberTiers();
      
      // Emit event to notify parent component
      this.$emit('member-tier-reactivated');
    } catch (error: any) {
      console.error('Error reactivating member tier:', error);
      this.$notification(NotificationOptions.error('Failed to reactivate member tier'));
    }
  }

  async validateStripeProduct() {
    if (!this.editingTier.stripeProductId || this.stripeProductValidation.isValidating) {
      this.stripeProductValidation.error = '';
      return;
    }

    this.stripeProductValidation.isValidating = true;
    this.stripeProductValidation.error = '';

    try {
      const params = new URLSearchParams({
        priceScheduleId: this.priceScheduleId.toString(),
        stripeProductId: this.editingTier.stripeProductId
      });
      
      if (this.editingTier.id) {
        params.append('excludeId', this.editingTier.id.toString());
      }

      const response = await axios.get(`/api/priceschedule/validate-stripe-product?${params.toString()}`);

      if (!response.data.isAvailable) {
        this.stripeProductValidation.error = 'This Stripe Product is already in use for this price schedule';
      }
    } catch (error) {
      console.error('Error validating Stripe product:', error);
      this.stripeProductValidation.error = 'Unable to validate Stripe Product';
    } finally {
      this.stripeProductValidation.isValidating = false;
    }
  }

  hideModal() {
    this.showModal = false;
    this.editingTier = new PriceScheduleMemberTier();
    this.stripeProductValidation = { error: '', isValidating: false };
  }
}
</script>

<style scoped>
.badge {
  font-size: 0.875em;
}

code {
  font-size: 0.875em;
  color: #e83e8c;
  background-color: #f8f9fa;
  padding: 2px 4px;
  border-radius: 3px;
}

.table td {
  vertical-align: middle;
}

.table-responsive {
  overflow-x: auto;
  overflow-y: visible;
}

h5 {
  margin-bottom: 1rem;
  color: #495057;
}

hr {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}
</style>